import {
  BodyText,
  Flex,
  Heading,
  IconButton,
  Tooltip,
  TooltipProvider,
} from '@utilitywarehouse/hearth-react';
import { CloseMediumIcon } from '@utilitywarehouse/hearth-react-icons';
import Player from '@vimeo/player';
import clsx from 'clsx';
import { FC, useEffect, useRef, useState } from 'react';

import { SnackbarVariant, useSnackbar } from '@hooks/useSnackbar';
import { useSyncState } from '@hooks/useSyncState';
import { useAppointmentContext } from '@providers/AppointmentProvider/AppointmentContext';
import { isIOS } from '@utils/device';

import './styles.scss';

export interface VideoSlideProps {
  vimeoId: string;
  vimeoHash?: string;
  headline?: string;
  subheadline?: string;
  isActive?: boolean;
  className?: string;
  onPlay?: () => void;
  onPause?: () => void;
}

const VideoSlide: FC<VideoSlideProps> = ({
  vimeoId,
  vimeoHash,
  headline,
  subheadline,
  isActive = true,
  className,
  onPlay,
  onPause,
}) => {
  const hasStartedRef = useRef(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const playerRef = useRef<Player | null>(null);
  const { hasControl, isSyncedAppointment } = useAppointmentContext();
  const snackbar = useSnackbar();

  const vimeoUrl = vimeoHash
    ? `https://player.vimeo.com/video/${vimeoId}?h=${vimeoHash}`
    : `https://player.vimeo.com/video/${vimeoId}`;

  const [isPlaying, setIsPlaying] = useSyncState<boolean>(
    `partner-presentation-video-${vimeoHash}-${isActive}`,
    false
  );

  const [disableVideoSyncHelperMessage, setDisableVideoSyncHelperMessage] =
    useState(false);

  useEffect(() => {
    if (!isActive) {
      setDisableVideoSyncHelperMessage(false);
      return;
    }

    if (isIOS()) {
      snackbar({
        message: 'You may need to unmute the video to hear audio',
        variant: SnackbarVariant.Warning,
        preventDuplicate: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  const isPlayingRef = useRef(isPlaying);
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  const syncPlaybackRef = useRef(true);
  const canTogglePlaybackRef = useRef(!isSyncedAppointment || hasControl);
  useEffect(() => {
    canTogglePlaybackRef.current = !isSyncedAppointment || hasControl;
  }, [isSyncedAppointment, hasControl]);

  useEffect(() => {
    if (!iframeRef.current) return;

    const player = new Player(iframeRef.current);
    playerRef.current = player;

    player.on('play', () => {
      setDisableVideoSyncHelperMessage(false);

      if (!canTogglePlaybackRef.current) {
        if (!syncPlaybackRef.current) return; // let user control locally

        // If we're not allowed to toggle the playback, and it's already playing,
        // then pause it again to prevent out-of-sync playback.
        if (!isPlayingRef.current) player.pause();
        return;
      }
      hasStartedRef.current = true;
      setIsPlaying(true);
      onPlay?.();
    });
    player.on('pause', () => {
      if (!canTogglePlaybackRef.current) {
        if (!syncPlaybackRef.current) return; // let user control locally

        // If we're not allowed to toggle the playback, and it's already paused,
        // then play it again to prevent out-of-sync playback.
        if (isPlayingRef.current) player.play();
        return;
      }
      setIsPlaying(false);
      onPause?.();
    });

    return () => {
      player.off('play');
      player.off('pause');
    };
  }, [onPlay, onPause, setIsPlaying]);

  useEffect(() => {
    if (!isActive && hasStartedRef.current) {
      playerRef.current?.pause();
    }
  }, [isActive]);

  useEffect(() => {
    if (!iframeRef.current) return;

    const player = playerRef.current;
    if (!player) return;

    if (!isSyncedAppointment || hasControl) return;
    if (!syncPlaybackRef.current) return;

    if (isPlaying) {
      player.play().catch(() => {
        disableVideoSync();
      });
    } else {
      player.pause();
    }
  }, [isPlaying, isSyncedAppointment, hasControl]);

  const disableVideoSync = () => {
    syncPlaybackRef.current = false;
    setDisableVideoSyncHelperMessage(true);
  };

  const dismissDisableVideoSyncHelperMessage = () => {
    setDisableVideoSyncHelperMessage(false);
  };

  const textBlock = (headline || subheadline) && (
    <Flex direction="column" gap="200" className="video-slide__header">
      {headline && (
        <Heading as="h2" className="video-slide__headline">
          {headline}
        </Heading>
      )}
      {subheadline && (
        <BodyText className="video-slide__subheadline">{subheadline}</BodyText>
      )}
    </Flex>
  );

  return (
    <Flex
      direction="column"
      justifyContent={{ mobile: 'center', desktop: 'start' }}
      className={clsx('video-slide', className)}
    >
      {textBlock}

      <div className="video-slide__container">
        <iframe
          ref={iframeRef}
          src={vimeoUrl}
          allow="autoplay; fullscreen"
          title="Video"
          className="video-slide__iframe"
        />

        <TooltipProvider>
          <Tooltip
            open={disableVideoSyncHelperMessage}
            align="topCenter"
            className="video-slide__tooltip"
            description={
              <Flex gap="100" alignItems="start">
                <BodyText>
                  We couldn't start the video automatically. Please tap play to
                  begin watching.
                </BodyText>
                <IconButton
                  variant="ghost"
                  size="sm"
                  label="Close"
                  onClick={dismissDisableVideoSyncHelperMessage}
                >
                  <CloseMediumIcon color="white" />
                </IconButton>
              </Flex>
            }
          >
            {/* Without this the tooltip won't be positioned correctly */}
            <span tabIndex={0} className="video-slide__tooltip-inner" />
          </Tooltip>
        </TooltipProvider>
      </div>
    </Flex>
  );
};

export { VideoSlide };
